import { defineComponent as _defineComponent } from 'vue'
import { ref, reactive, computed, onMounted, watch } from 'vue'
import type { Component } from 'vue'
import { setDrawerHost, type DrawerOptions, type DrawerPayload } from '@/lib/composable/drawer/drawerHostBridge'

type ResolvedOptions = Required<DrawerOptions>


export default /*@__PURE__*/_defineComponent({
  __name: 'DrawerHost',
  setup(__props, { expose: __expose }) {

const componentRef = ref<any>(null)

const DEFAULTS: ResolvedOptions = {
  width: 420,
  persistent: false,
  location: 'end'
}

const isOpen = ref(false)
const registry = new Map<string, Component>()
const wasOpen = ref(false)

const state = reactive<{
  component: string | Component | null
  props: Record<string, any>
  options: ResolvedOptions
  onClose?: () => void
}>({
  component: null,
  props: {},
  options: { ...DEFAULTS }
})

function resolve(c: string | Component | null): Component | null {
  if (!c) return null
  if (typeof c === 'string') return registry.get(c) ?? null
  return c
}

const resolvedComponent = computed(() => resolve(state.component))

function open(payload: DrawerPayload) {
  state.component = payload.component
  state.props = payload.props ?? {}
  state.options = { ...DEFAULTS, ...(payload.options ?? {}) }
  state.onClose = payload.onClose
  isOpen.value = true
  wasOpen.value = true
}

function close() {
  if (!isOpen.value) return
  isOpen.value = false
  wasOpen.value = false
  // call onClose after drawer animation completes
  requestAnimationFrame(() => setTimeout(() => state.onClose?.(), 0))
}

// Watch for drawer closing via Escape key or other external means
watch(isOpen, (newValue: boolean, oldValue: boolean) => {
  // If drawer was open and is now closed (e.g., via Escape key)
  if (wasOpen.value && oldValue === true && newValue === false) {
    wasOpen.value = false
    // call onClose after drawer animation completes
    requestAnimationFrame(() => setTimeout(() => state.onClose?.(), 0))
  }
  // Update wasOpen to track current state
  if (newValue === true) {
    wasOpen.value = true
  }
})

function register(name: string, comp: Component) {
  registry.set(name, comp)
}

function unregister(name: string) {
  registry.delete(name)
}

function getComponentInstance() {
  return componentRef.value
}

const api = { open, close, register, unregister, getComponentInstance }
__expose(api)

onMounted(() => {
  // Publish API to the shared bridge (no window usage)
  setDrawerHost(api)
})

const __returned__ = { componentRef, DEFAULTS, isOpen, registry, wasOpen, state, resolve, resolvedComponent, open, close, register, unregister, getComponentInstance, api }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})